/**
 * Visual overlay for atom node field changes in diff mode.
 *
 * Wraps the atom field's NodeView content with a colored border, background,
 * and status badge indicating whether the field was added, removed, or changed.
 * For 'modified' status, also shows the old value with strikethrough.
 */

import { cn } from "@/lib/utils";

type DiffStatus = "inserted" | "deleted" | "modified";

interface DiffFieldOverlayProps {
  diffStatus: DiffStatus;
  oldValueLabel?: string | null;
  children: React.ReactNode;
}

const statusConfig: Record<
  DiffStatus,
  { border: string; bg: string; badge: string; badgeLabel: string }
> = {
  inserted: {
    border: "border-l-4 border-l-[oklch(0.55_0.15_145)]",
    bg: "bg-[oklch(0.92_0.08_145)]",
    badge: "bg-[oklch(0.55_0.15_145)] text-white",
    badgeLabel: "Added",
  },
  deleted: {
    border: "border-l-4 border-l-[oklch(0.55_0.15_25)]",
    bg: "bg-[oklch(0.92_0.08_25)] opacity-70",
    badge: "bg-[oklch(0.55_0.15_25)] text-white",
    badgeLabel: "Removed",
  },
  modified: {
    border: "border-l-4 border-l-[oklch(0.55_0.15_85)]",
    bg: "bg-[oklch(0.95_0.05_85)]",
    badge: "bg-[oklch(0.55_0.15_85)] text-white",
    badgeLabel: "Changed",
  },
};

export function DiffFieldOverlay({ diffStatus, oldValueLabel, children }: DiffFieldOverlayProps) {
  const config = statusConfig[diffStatus];

  return (
    <div className={cn("rounded-sm pl-3 py-1 -ml-3", config.border, config.bg)}>
      <div className="flex items-center gap-2">
        <div className="flex-1">{children}</div>
        <div className="flex items-center gap-2 shrink-0 pr-2">
          {diffStatus === "modified" && oldValueLabel && (
            <span className="text-xs text-muted-foreground line-through">{oldValueLabel}</span>
          )}
          <span className={cn("text-[10px] font-semibold px-1.5 py-0.5 rounded", config.badge)}>
            {config.badgeLabel}
          </span>
        </div>
      </div>
    </div>
  );
}
